import React, {Component} from 'react'
import ReactDOM from 'react-dom'
import propTypes from 'prop-types'

const styles = {
  root: {
    position: 'relative', // anchor-point
  },
  dragLayer: {
    position: 'absolute',
    zIndex: 999,
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    overflow: 'hidden',
  },
  dragElement: {
    position: 'absolute',
    right: 0,
    bottom: 0,
    zIndex: 999,
  },
}

class DragLayerContent extends Component {
  shouldComponentUpdate = () => {
    return !this.props.preventUpdate
  }

  render() {
    return this.props.children
  }
}

DragLayerContent.propTypes = {
  preventUpdate: propTypes.bool,
  children: propTypes.any,
}

class DragLayer extends Component {
  state = {
    active: false,      // true if element is currently being dragged
    root: null,         // rect of dragging element when started drag
    offset: null,       // rect of drag layer parent when started drag
    start: null,        // x,y coords of cursor when started drag
    current: null,      // x,y coords of dragging element during drag
    minDistance: null,
    axis: null,
    onStart: null,
    onDrag: null,
    onDrop: null,
    previewElement: null,  // the element being displayed under the cursor whilst dragging
    previewWidth: null,
    previewHeight: null,
    drops: [],
  }

  dropComponents = []

  addDropComponent = (component) => {
    this.dropComponents = [...this.dropComponents, component]
  }

  removeDropComponent = (component) => {
    this.dropComponents = this.dropComponents.filter(d => d !== component)
  }

  startDrag = (params) => {
    const {
      data,
      element,
      x,
      y,
      previewElement,
      previewWidth,
      previewHeight,
      minDistance = 5,
      axis = '',
      onStart,
      onDrag,
      onDrop,
    } = params

    const pos = {x, y}
    const root = element.getBoundingClientRect()

    const parent = this.root//.parentElement
    const offset = parent.getBoundingClientRect()
    const bounds = {
      top: 0,
      left: 0,
      right: offset.width - root.width,
      bottom: offset.height - root.height,
    }

    // Initialise drop zones
    const drops = this.dropComponents.map(component => {
      let bounds = null
      try {
        const elm = ReactDOM.findDOMNode(component)  // eslint-disable-line react/no-find-dom-node
        if (elm && this.isVisible(elm)) bounds = elm.getBoundingClientRect()
      } catch (e) {
      }

      return {
        component,
        bounds,
        active: false,
      }
    })

    this.setState({
      data,
      root,
      start: pos,
      offset,
      bounds,
      minDistance,
      axis,
      previewElement,
      previewWidth,
      previewHeight,
      drops,
      onStart,
      onDrag,
      onDrop,
    })

    window.addEventListener('mousemove', this.mouseMove)
    window.addEventListener('mouseup', this.mouseUp)
  }

  isVisible = (elm) => {
    return !!(elm.offsetWidth || elm.offsetHeight || elm.getClientRects().length)
  }

  canDrag = (e) => {
    if (Math.abs(e.clientX - this.state.start.x) > this.state.minDistance) return true
    if (Math.abs(e.clientY - this.state.start.y) > this.state.minDistance) return true
    return false
  }

  applyBounds = (value, min, max) => {
    if (value < min) return min
    if (value > max) return max
    return value
  }

  getPos = (e, current = this.state.current) => {
    const {offset, root} = this.state

    return {
      top: current.y,
      left: current.x,
      width: root.width,
      height: root.height,
      cursor: {x: e.clientX - offset.left, y: e.clientY - offset.top},
      root: {
        top: root.top - offset.top,
        left: root.left - offset.left,
      },
    }
  }

  mouseMove = (e) => {
    let {active, drops} = this.state
    const {data, root, offset, start, bounds, axis, onStart, onDrag} = this.state

    if (!active && start && this.canDrag(e)) {
      if (onStart) onStart()
      active = true
    }

    if (active) {

      let x, y
      if (axis === 'x') {
        y = root.top - offset.top
      } else {
        y = e.clientY - start.y + root.top - offset.top
        if (bounds) y = this.applyBounds(y, bounds.top, bounds.bottom)
      }

      if (axis === 'y') {
        x = root.left - offset.left
      } else {
        x = e.clientX - start.x + root.left - offset.left
        if (bounds) x = this.applyBounds(x, bounds.left, bounds.right)
      }

      drops = drops.map(drop => ({
        ...drop,
        active: this.isDropActive(e, drop),
        wasActive: drop.active,
      }))

      this.setState({
        active: true,
        current: {x, y},
        drops,
      })

      if (onDrag) onDrag(this.getPos(e, {x, y}))

      // Check for drop zones
      drops.filter(drop => !drop.active && drop.wasActive).forEach(drop => {
        drop.component.onDragOut(e, data)
      })
      drops.filter(drop => drop.active && !drop.wasActive).forEach(drop => {
        drop.component.onDragOver(e, data)
      })
    }
    if (start) e.preventDefault()
  }

  mouseUp = (e) => {
    const {active, data, start, drops, onDrop} = this.state

    if (start) {
      window.removeEventListener('mousemove', this.mouseMove)
      window.removeEventListener('mouseup', this.mouseUp)

      const dropPos = active ? this.getPos(e) : null
      this.setState({active: false, start: null, root: null, current: null}, () => {

        // Drop after resetting active to enable UI updates again
        if (dropPos && onDrop) onDrop(dropPos)

        // Check for drop zones
        drops.filter(drop => drop.active).forEach(drop => {
          drop.component.onDrop(e, data)
        })
      })
    }
  }

  isDropActive = (e, drop) => {
    const {bounds} = drop
    return (
      bounds &&
      e.clientX >= bounds.left &&
      e.clientX < bounds.right &&
      e.clientY >= bounds.top &&
      e.clientY < bounds.bottom
    )
  }

  // Required by React's legacy context API (see DragLayer.childContextTypes below).
  // React calls this implicitly — it will appear "unused" to IDEs but must not be removed.
  // noinspection JSUnusedGlobalSymbols
  getChildContext = () => {
    return {
      dragLayer: this,
    }
  }

  render() {
    const {children, className, preventUpdateWhileDragging, style} = this.props
    const {active, current, previewElement, previewWidth, previewHeight} = this.state

    let dragLayer = null
    if (active) {

      const dragStyle = {
        ...styles.dragElement,
        top: current.y,
        left: current.x,
        width: previewWidth,
        height: previewHeight,
      }

      dragLayer = (
        <div style={styles.dragLayer}>
          <div style={dragStyle}>{previewElement}</div>
        </div>
      )
    }

    return (
      <div
        ref={e => {
          this.root = e
        }}
        style={{...styles.root, ...style}}
        className={`dragLayer ${className}`}
      >
        <DragLayerContent preventUpdate={preventUpdateWhileDragging && active}>
          {children}
        </DragLayerContent>
        {dragLayer}
      </div>
    )
  }
}

DragLayer.propTypes = {
  children: propTypes.element,
  className: propTypes.string,
  preventUpdateWhileDragging: propTypes.bool,
  style: propTypes.object,
}

DragLayer.defaultProps = {
  preventUpdateWhileDragging: false,
}

DragLayer.childContextTypes = {
  dragLayer: propTypes.object,
}

export default DragLayer
